import { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineArrowsSwapHorizontalIcon from 'LuxKit/Icons/line/LineArrowsSwapHorizontalIcon'
import useOrderFlightDetails from 'hooks/Flights/useOrderFlightDetails'
import { nonNullable } from 'lib/array/arrayUtils'
import {
  deriveFlightDisplayData,
  formatFlightFallbackTitle,
  formatFlightTypeLabel,
} from './flightDisplayUtils'
import { formatOrderTileDates } from './orderTileDisplayUtils'
import AddedBookingTile from './AddedBookingTile'
import { useTripDateFormatters } from './useTripDateFormatters'

const RETURN_ROUTE_ICON = <LineArrowsSwapHorizontalIcon size="S" />
const ONE_WAY_ROUTE_ICON = <LineArrowRightIcon size="S" />

const AirlineLogo = styled.img`
  inline-size: ${rem(28)};
  block-size: ${rem(28)};
  object-fit: contain;
  flex-shrink: 0;
`

interface Props {
  itemId: string
  orderId: string
}

export default function AddedFlightBookingTile({ itemId, orderId }: Props) {
  const intl = useIntl()
  const formatters = useTripDateFormatters()
  const [{ flightDetails }] = useOrderFlightDetails(orderId, [itemId])

  const {
    departureDate,
    arrivalDate,
    departureLabel,
    arrivalLabel,
    airlineLogoUrl,
    isReturnFlight,
    isDirectFlight,
  } = deriveFlightDisplayData(flightDetails)

  const subtitle = nonNullable([
    formatOrderTileDates(formatters, departureDate, isReturnFlight ? arrivalDate : undefined),
    formatFlightTypeLabel(intl, isReturnFlight, isDirectFlight),
  ]).join(formatMiddleDotSeparator(intl))

  const title = useMemo(() => {
    if (!departureLabel || !arrivalLabel) {
      return formatFlightFallbackTitle(intl)
    }
    return <>{departureLabel} {isReturnFlight ? RETURN_ROUTE_ICON : ONE_WAY_ROUTE_ICON} {arrivalLabel}</>
  }, [arrivalLabel, departureLabel, intl, isReturnFlight])

  const thumbnail = useMemo(() => airlineLogoUrl && <AirlineLogo src={airlineLogoUrl} alt="" aria-hidden="true" />, [airlineLogoUrl])

  return <AddedBookingTile title={title} subtitle={subtitle} thumbnail={thumbnail} />
}
